import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import {
    isNameError,
    isFieldError,
    isValidExpiryDate,
} from "../../../helpers/input-validations";
import CustomInput from "../../common/CustomInput";
import Button from "../../common/Button";
import { updateReelMember } from "../../../redux/modules/user/actions";

const SECONDARY_GOV_ID_OPTIONS = [
    { value: "Passport", label: "Passport" },
    { value: "Health Card", label: "Health Card" },
    { value: "Citizenship Card", label: "Citizenship Card" },
    { value: "Permanent Resident Card", label: "Permanent Resident Card" },
];

const BACKGROUND_CHECK_OPTIONS = [
    { value: "Complete", label: "Complete" },
    { value: "Not Required", label: "Not Required" },
    { value: "In Progress", label: "In Progress" },
];

const HIPPA_CERT_OPTIONS = [
    { value: "Complete", label: "Complete" },
    { value: "Not Required", label: "Not Required" },
    { value: "In Progress", label: "In Progress" },
];

const PersonalInfoEdit = ({ onClose, user }) => {
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [clicked, setClicked] = useState(false);

    const [lastName, setLastName] = useState(user?.lastName ?? "");
    const [middleName, setMiddleName] = useState(user?.middleName ?? "");
    const [firstName, setFirstName] = useState(user?.firstName ?? "");
    const [driverLicence, setDriverLicence] = useState(user?.driverLicence ?? "");
    const [driverLicenceExpiry, setDriverLicenceExpiry] = useState(user?.driverLicenceExpiry ?? "");
    const [secondaryGovernmentID, setSecondaryGovernmentID] = useState(
        user?.secondaryGovernmentID ?? user?.SecondaryGovernmentID ?? ""
    );
    const [secondaryGovernmentIDNumber, setSecondaryGovernmentIDNumber] = useState(
        user?.secondaryGovernmentIDNumber ?? ""
    );
    const [secondaryGovernmentIDExpiry, setSecondaryGovernmentIDExpiry] = useState(
        user?.secondaryGovernmentIDExpiry ?? user?.SecondaryGovernmentIDExpiry ?? ""
    );
    const [backgroundCheck, setBackgroundCheck] = useState(
        user?.backgroundCheckPoliceClearance ?? user?.backgroundCheck ?? "In Progress"
    );
    const [hippaCertification, setHippaCertification] = useState(user?.hippaCertification ?? "");
    const [hippaCertificationExpiryDate, setHippaCertificationExpiryDate] = useState(
        user?.hippaCertificationExpiryDate ?? ""
    );
    const [UploadPhoto, setUploadPhoto] = useState(null);
    const [uploadPhotoPreview, setUploadPhotoPreview] = useState(
        user?.photo ? `data:image/jpeg;base64,${user.photo}` : null
    );
    const [uploadPhotoErrorMessage, setUploadPhotoErrorMessage] = useState("");
    const [personalInfoComments, setPersonalInfoComments] = useState(user?.personalInfoComments ?? "");

    const [lastNameError, setLastNameError] = useState(false);
    const [firstNameError, setFirstNameError] = useState(false);
    const [driverLicenceError, setDriverLicenceError] = useState(false);
    const [driverLicenceExpiryError, setDriverLicenceExpiryError] = useState(false);
    const [secondaryGovernmentIDError, setSecondaryGovernmentIDError] = useState(false);
    const [secondaryGovernmentIDExpiryError, setSecondaryGovernmentIDExpiryError] = useState(false);
    const [backgroundCheckError, setBackgroundCheckError] = useState(false);
    const [hippaCertificationError, setHippaCertificationError] = useState(false);
    const [hippaCertificationExpiryDateError, setHippaCertificationExpiryDateError] = useState(false);

    const fieldsToCheck = [
        { field: lastName, checkFn: isNameError, setErrorFn: setLastNameError, alter: false },
        { field: firstName, checkFn: isNameError, setErrorFn: setFirstNameError, alter: false },
        { field: driverLicence, checkFn: isNameError, setErrorFn: setDriverLicenceError, alter: false },
        { field: driverLicenceExpiry, checkFn: isValidExpiryDate, setErrorFn: setDriverLicenceExpiryError, alter: true },
        { field: secondaryGovernmentID, checkFn: isFieldError, setErrorFn: setSecondaryGovernmentIDError, alter: false },
        { field: secondaryGovernmentIDExpiry, checkFn: isValidExpiryDate, setErrorFn: setSecondaryGovernmentIDExpiryError, alter: true },
        { field: backgroundCheck, checkFn: isFieldError, setErrorFn: setBackgroundCheckError, alter: false },
        { field: hippaCertification, checkFn: isFieldError, setErrorFn: setHippaCertificationError, alter: false },
        { field: hippaCertificationExpiryDate, checkFn: isValidExpiryDate, setErrorFn: setHippaCertificationExpiryDateError, alter: true },
    ];

    useEffect(() => {
        if (clicked) {
            fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
                const hasError = alter ? !checkFn(field) : checkFn(field);
                setErrorFn(hasError);
            });
        }
    }, [clicked, ...fieldsToCheck.map(({ field }) => field)]);

    const manageStep = async () => {
        setClicked(true);
        let allFieldsValid = true;

        fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
            const hasError = alter ? !checkFn(field) : checkFn(field);
            setErrorFn(hasError);
            if (hasError) allFieldsValid = false;
        });

        if (allFieldsValid) {
            try {
                setIsLoading(true);
                // Exclude large binary fields (photo, signature) — they are managed via dedicated endpoints
                const { photo, signature, ...userWithoutBinaries } = user;
                await dispatch(updateReelMember(user.userId, {
                    ...userWithoutBinaries,
                    lastName,
                    middleName,
                    firstName,
                    driverLicence,
                    driverLicenceExpiry: driverLicenceExpiry || null,
                    secondaryGovernmentID,
                    secondaryGovernmentIDExpiry: secondaryGovernmentIDExpiry || null,
                    secondaryGovernmentIDNumber,
                    backgroundCheck,
                    backgroundCheckPoliceClearance: backgroundCheck,
                    hippaCertification,
                    hipaaComplianceCertification: hippaCertification,
                    hippaCertificationExpiryDate: hippaCertificationExpiryDate || null,
                    UploadPhoto,
                    personalInfoComments,
                }));
                onClose();
            } catch (error) {
                console.error('Error updating user:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <div className="flex flex-col h-[75vh]">
            <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-12 gap-3 mt-2">
                    <CustomInput
                        name="lastName"
                        className="col-span-4"
                        label="Last Name"
                        placeholder="Enter Last Name"
                        type="text"
                        value={lastName}
                        required
                        onChange={(e) => setLastName(e.target.value)}
                        error={lastNameError}
                        capitalizeFirstLetter={true}
                    />
                    <CustomInput
                        name="middleName"
                        className="col-span-4"
                        label="Middle Name"
                        placeholder="Enter Middle Name"
                        type="text"
                        value={middleName}
                        onChange={(e) => setMiddleName(e.target.value)}
                        capitalizeFirstLetter={true}
                    />
                    <CustomInput
                        name="firstName"
                        className="col-span-4"
                        label="First Name"
                        placeholder="Enter First Name"
                        type="text"
                        value={firstName}
                        required
                        onChange={(e) => setFirstName(e.target.value)}
                        error={firstNameError}
                        capitalizeFirstLetter={true}
                    />

                    <CustomInput
                        name="driverLicence"
                        className="col-span-6"
                        label="Driver Licence"
                        placeholder="Enter Driver Licence"
                        type="text"
                        value={driverLicence}
                        required
                        onChange={(e) => setDriverLicence(e.target.value)}
                        error={driverLicenceError}
                    />
                    <CustomInput
                        name="driverLicenceExpiry"
                        className="col-span-6"
                        label="Expiry Date"
                        type="date"
                        value={driverLicenceExpiry}
                        required
                        onChange={(value) => setDriverLicenceExpiry(value)}
                        error={driverLicenceExpiryError}
                    />

                    <CustomInput
                        options={SECONDARY_GOV_ID_OPTIONS}
                        name="secondaryGovernmentID"
                        className="col-span-6"
                        label="Secondary Government ID"
                        type="dropdown"
                        value={secondaryGovernmentID}
                        required
                        onChange={(e) => setSecondaryGovernmentID(e.value)}
                        error={secondaryGovernmentIDError}
                    />
                    <CustomInput
                        name="secondaryGovernmentIDExpiry"
                        className="col-span-6"
                        label="Expiry Date"
                        type="date"
                        value={secondaryGovernmentIDExpiry}
                        required
                        onChange={(value) => setSecondaryGovernmentIDExpiry(value)}
                        error={secondaryGovernmentIDExpiryError}
                    />

                    {secondaryGovernmentID && (
                        <CustomInput
                            name="secondaryGovernmentIDNumber"
                            className="col-span-12"
                            label="Secondary Government ID Number"
                            placeholder="Enter Secondary Government ID Number"
                            type="text"
                            value={secondaryGovernmentIDNumber}
                            onChange={(e) => setSecondaryGovernmentIDNumber(e.target.value)}
                        />
                    )}

                    <CustomInput
                        options={BACKGROUND_CHECK_OPTIONS}
                        name="backgroundCheck"
                        className="col-span-12"
                        label="Background Check - Police Clearance"
                        type="dropdown"
                        value={backgroundCheck}
                        required
                        onChange={(e) => setBackgroundCheck(e.value)}
                        error={backgroundCheckError}
                    />

                    <CustomInput
                        options={HIPPA_CERT_OPTIONS}
                        name="hippaCertification"
                        className="col-span-6"
                        label="HIPPA Compliancy Certification"
                        type="dropdown"
                        value={hippaCertification}
                        required
                        onChange={(e) => setHippaCertification(e.value)}
                        error={hippaCertificationError}
                    />
                    <CustomInput
                        name="hippaCertificationExpiryDate"
                        className="col-span-6"
                        label="HIPPA Compliancy Certification - Expiry Date"
                        type="date"
                        value={hippaCertificationExpiryDate}
                        required
                        onChange={(value) => setHippaCertificationExpiryDate(value)}
                        error={hippaCertificationExpiryDateError}
                    />

                    <CustomInput
                        name="UploadPhoto"
                        className="col-span-12"
                        label="Upload Photo"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        maxSize={5 * 1024 * 1024}
                        onChange={(file) => {
                            setUploadPhotoErrorMessage("");

                            if (file && file.size > 5 * 1024 * 1024) {
                                setUploadPhoto(file);
                                setUploadPhotoPreview(null);
                                setUploadPhotoErrorMessage("Image is too large. Maximum size is 5MB.");
                                return;
                            }

                            setUploadPhoto(file);
                            if (file) {
                                setUploadPhotoPreview(URL.createObjectURL(file));
                            } else {
                                setUploadPhotoPreview(user?.photo ? `data:image/jpeg;base64,${user.photo}` : null);
                            }
                        }}
                    />
                    {uploadPhotoErrorMessage && (
                        <div className="col-span-12 -mt-2 text-sm text-red-500">
                            {uploadPhotoErrorMessage}
                        </div>
                    )}
                    {uploadPhotoPreview && (
                        <div className="col-span-12 mt-3">
                            <img
                                src={uploadPhotoPreview}
                                alt="Selected"
                                className="max-h-40 rounded-md border border-gray-200 object-contain"
                            />
                        </div>
                    )}

                    <CustomInput
                        name="personalInfoComments"
                        className="col-span-12"
                        label="Comments"
                        placeholder="Enter Comments"
                        type="text"
                        value={personalInfoComments}
                        onChange={(e) => setPersonalInfoComments(e.target.value)}
                    />
                </div>
            </div>
            <div className="flex items-center justify-center py-6 mt-auto">
                <div className="flex items-center justify-center space-x-5">
                    <Button
                        className="min-w-[200px]"
                        text="Cancel"
                        type="secondary"
                        onClick={() => onClose(false)}
                    />
                    <Button
                        className="min-w-[200px]"
                        text={isLoading ? "Updating..." : "Update & Save"}
                        type="primary"
                        onClick={() => manageStep()}
                        disabled={isLoading}
                    />
                </div>
            </div>
        </div>
    );
};

export default PersonalInfoEdit;
